import React, { Dispatch, SetStateAction, useState } from "react"
import {
  CartContainer,
  CartItems,
  Label
} from "../../Order/ManageOrder/DeliverOrder/Cart/Cart.styled"
import Totals from "../../Order/ManageOrder/DeliverOrder/Cart/Totals"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit,
  OrderOperationSearchHits
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel, OrderTotal } from "../Shared.styled"
import { Link } from "react-router-dom"
import OperationOrderLine from "./OperationOrderLine"
import OperationShippingFee from "./OperationShippingFee"
import OperationGiftCardProduct from "./OperationGiftCardProduct"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import StartOperation from "./StartOperation"
import { ApolloQueryResult, useMutation } from "@apollo/client"
import START_DELIVERY from "../../../graphql/mutations/order/deliveries/StartDelivery"
import { handleErrorMessages } from "helpers/errors"
import FailedDelivery from "../../Order/ManageOrder/DeliverOrder/FailedDelivery"

type Props = {
  operation: OrderOperationSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  refetchOperations: () => Promise<ApolloQueryResult<OrderOperationSearchHits>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  isOperationBlocked?: boolean
  blockedReason?: string
}

const DeliveryAction = ({
  operation,
  order,
  orderHistory,
  getCustomCode,
  setShow,
  refetchOperations,
  setCreatedOperations,
  setFailedOperations,
  isOperationBlocked = false,
  blockedReason = ""
}: Props) => {
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentDelivery>({
    actionType: graphql.InputActionType.Auto
  })
  const [shippingAction, setShippingAction] = useState<graphql.InputActionShipping>({
    actionType: graphql.InputActionType.Auto
  })

  const delivery = order.Deliveries.find((delivery) => delivery.id === operation.id)

  const [startDelivery, { loading }] = useMutation(START_DELIVERY, {
    onCompleted: () => {
      setShow(false)
      setCreatedOperations((prev) => prev?.filter((hit) => hit.id !== operation?.id))
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const startOrderDelivery = () => {
    startDelivery({
      variables: {
        deliveryId: delivery?.id,
        input: {
          ...(delivery?.bonus && { bonus: bonusAction }),
          ...(delivery?.giftCards &&
            delivery?.giftCards?.length > 0 && { giftCard: giftCardAction }),
          shipping: shippingAction,
          payment: paymentAction
        }
      }
    })
  }

  const getOrderLine = (deliveryLineId: string) => {
    return order?.orderLines?.find((orderLine) => orderLine.id === deliveryLineId)
  }

  const getShippingFee = (deliveryFeeId: string) => {
    return order?.shippingFees?.find((fee) => fee.id === deliveryFeeId)
  }

  const isDeliveryFailed = delivery?.failed && !delivery.completed

  if (isDeliveryFailed)
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <FailedDelivery
          failedDelivery={delivery}
          currencyCode={order.currencyCode}
          refetch={refetchOperations}
          orderHistory={orderHistory.history}
          setFailedOperations={setFailedOperations}
          isOperationBlocked={isOperationBlocked}
          blockedReason={blockedReason}
        />
      </>
    )

  return (
    <>
      <CartContainer key={delivery?.id}>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <CartItems>
          {delivery?.orderLines.map((ol) => (
            <OperationOrderLine
              key={ol.orderLineId}
              orderLine={getOrderLine(ol.orderLineId)}
              orderQuantity={ol.quantity}
            />
          ))}
          {delivery?.shippingFees?.map((shippingFee) => (
            <OperationShippingFee
              shippingFee={getShippingFee(shippingFee.shippingFeeId)}
              key={shippingFee.shippingFeeId}
            />
          ))}
          {delivery?.giftCardProducts?.map((giftCardProduct) => (
            <OperationGiftCardProduct
              giftCardProduct={giftCardProduct}
              key={giftCardProduct.giftCardProductId}
            />
          ))}
          <Reason reason={delivery?.reason} getCustomCode={getCustomCode} />
          <OrderTotal>
            <div>Order total</div>{" "}
            <div>
              <Money
                amount={order.totals.grandTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </div>
          </OrderTotal>
          <Label>To be delivered: </Label>
          {delivery?.totals && (
            <Totals totals={delivery?.totals} currencyCode={order?.currencyCode} />
          )}
        </CartItems>
        <StartOperation
          operation={delivery}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction,
            shippingAction,
            setShippingAction
          }}
          startOperation={startOrderDelivery}
          loading={loading}
          disabled={isOperationBlocked}
          blockedReason={blockedReason}
        />
      </CartContainer>
    </>
  )
}

export default DeliveryAction
